import { Children, isValidElement, memo, type ReactNode } from "react";
import Markdown, { defaultUrlTransform, type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const components: Components = {
  a: ({ href, children }) => href
    ? <a href={href} target="_blank" rel="noreferrer noopener">{children}</a>
    : <span>{children}</span>,
  pre: ({ children }) => {
    const code = Children.only(children);
    const text = isValidElement<{ children?: ReactNode }>(code) ? code.props.children : children;
    // CommonMark adds one terminal newline while rendering a fenced block.
    // Keep the established QR / code display's exact inner whitespace.
    return <pre className="fence" tabIndex={0}>{typeof text === "string" && text.endsWith("\n") ? text.slice(0, -1) : text}</pre>;
  },
  table: ({ children }) => <div className="markdown-table" tabIndex={0} role="region" aria-label="表格"><table>{children}</table></div>,
  img: ({ src, alt }) => <img src={src} alt={alt || ""} loading="lazy" referrerPolicy="no-referrer" />,
};
const safeUrl = (url: string) => url.startsWith("file://") ? url : defaultUrlTransform(url);

type TextNode = { type: string; value?: string; children?: TextNode[] };
/**
 * A writer's line breaks, kept the way a chat keeps them. CommonMark folds a
 * single newline inside a paragraph into a space, which ran the Agent's
 * numbered choices, and its 「身份：」「给谁看：」 lines, into one block. Each
 * such newline becomes a `break`, as `remark-breaks` does; code is not a text
 * node and keeps its own.
 */
function keepLineBreaks() {
  const split = (node: TextNode): void => {
    if (!node.children) return;
    node.children = node.children.flatMap((child): TextNode[] => {
      if (child.type !== "text" || !child.value?.includes("\n")) {
        split(child);
        return [child];
      }
      return child.value.split(/\r?\n/).flatMap((line, index): TextNode[] =>
        index ? [{ type: "break" }, { type: "text", value: line }] : [{ type: "text", value: line }]);
    });
  };
  return (tree: TextNode) => split(tree);
}
const documentPlugins = [remarkGfm];
const conversationPlugins = [remarkGfm, keepLineBreaks];

/**
 * Raw HTML remains text. No HTML parser or executable markup is enabled.
 * `breaks` is for words said in a conversation, where a newline is the
 * writer's; a page's text keeps CommonMark's folding.
 */
export const MarkdownText = memo(function MarkdownText({ text, breaks = false }: { text: string; breaks?: boolean }) {
  return <Markdown remarkPlugins={breaks ? conversationPlugins : documentPlugins} components={components} urlTransform={safeUrl}>{text}</Markdown>;
});

type SummaryNode = { type: string; value?: string; alt?: string; children?: SummaryNode[] };
const separated = new Set(["root", "list", "listItem", "blockquote", "table", "tableRow"]);
function excerpt(node: SummaryNode): string {
  if (node.type === "html" || node.type === "definition" || node.type === "footnoteDefinition") return "";
  if (node.type === "image" || node.type === "imageReference") return node.alt || "";
  if (node.type === "break") return " ";
  if (node.children) return node.children.map(excerpt).join(separated.has(node.type) ? " " : "");
  return node.value || "";
}
/** Use the same parsed Markdown tree; a preview never loads images or nests links. */
function summarizeMarkdown() {
  return (tree: SummaryNode) => {
    const chars = Array.from(excerpt(tree).replace(/\s+/g, " ").trim());
    const value = chars.length ? chars.slice(0, 160).join("") + (chars.length > 160 ? "…" : "") : "继续这段对话";
    tree.children = [{ type: "paragraph", children: [{ type: "text", value }] }];
  };
}
const summaryComponents: Components = { p: ({ children }) => <span>{children}</span> };
export const MarkdownSummary = memo(function MarkdownSummary({ text }: { text: string }) {
  return <Markdown remarkPlugins={[remarkGfm, summarizeMarkdown]} components={summaryComponents}>{text}</Markdown>;
});
